import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  IconButton,
  Circle,
  Spinner,
  Tooltip
} from '@chakra-ui/react';
import {
  FiFileText,
  FiUser,
  FiChevronDown,
  FiX
} from 'react-icons/fi';

// Servicios reales del sistema
import { getAllSurveys } from 'services/surveyService';
import { searchCustomers } from 'services/unifiedCustomersSearch';
import { useUserAuth, canUserAccessCategory, getSiteConfig } from 'utils/site';

// Modal de formulario (igual que en el perfil)
import ModalAddSurveyResponse from 'components/Modal/ModalAddSurveyResponse';
import UnifiedSearchInput from 'components/Search/UnifiedSearchInput';
import AnchoredDropdown from 'components/Search/AnchoredDropdown';

// Utilidad debounce
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const getInitials = (first = '', last = '') =>
  ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';

const FormSelectorModal = ({ isOpen, onClose, onSuccess, onError }) => {
  const { user: currentUser } = useUserAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Formularios de "Ficha de atención" disponibles (mismo patrón que Customers.js / Agenda)
  const [atencionForms, setAtencionForms] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [formMenuOpen, setFormMenuOpen] = useState(false);

  const searchAnchorRef = useRef(null);
  const formBtnRef = useRef(null);

  const resetForm = useCallback(() => {
    setSelectedCustomer(null);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedSurvey(null);
    setShowSurveyModal(false);
    setFormMenuOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  // Cargar formularios de categoría "atencion" disponibles para el rol actual
  useEffect(() => {
    if (!isOpen || !currentUser?.role || !canUserAccessCategory(currentUser.role, 'atencion')) return;

    const loadAtencionForms = async () => {
      try {
        const siteConfig = JSON.parse(getSiteConfig() || '{}');
        const glosario = {
          'Pagos CapitalWeb CL': 'pagos-capitalweb-cl',
          'Pagos BlasPascal CL': 'blaspascal',
          'Pagos Testing': 'blaspascal'
        };
        const siteSlug = glosario[siteConfig.name] || 'blaspascal';
        const surveys = await getAllSurveys(siteSlug, { userAuth: currentUser });
        setAtencionForms((surveys || []).filter(survey => survey.category === 'atencion'));
      } catch (error) {
        setAtencionForms([]);
        onError && onError('No se pudieron cargar las fichas de atención');
      }
    };

    loadAtencionForms();
  }, [isOpen, currentUser]);

  // Búsqueda de usuarios (servicio unificado)
  const handleUserSearch = async (term) => {
    if (!term || term.trim().length < 1) {
      setSearchResults([]);
      return;
    }
    setLoadingSearch(true);
    try {
      const results = await searchCustomers(term, 10);
      setSearchResults(results || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const debounceSearch = React.useCallback(debounce((term) => handleUserSearch(term), 400), []);

  useEffect(() => {
    if (searchTerm.trim()) {
      debounceSearch(searchTerm);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, debounceSearch]);

  // Seleccionar alumno encontrado — todavía no abre ningún formulario
  const handleCustomerSelect = (customer) => {
    const normalizedCustomer = {
      id: customer.id,
      first_name: customer.first_name || customer.name?.split(' ')[0] || '',
      last_name: customer.last_name || customer.name?.split(' ').slice(1).join(' ') || '',
      username: customer.username,
      email: customer.email || '',
      ...customer
    };

    setSelectedCustomer(normalizedCustomer);
    setSearchTerm(customer.name || customer.display_text || `${normalizedCustomer.first_name} ${normalizedCustomer.last_name}`);
    setSearchResults([]);
  };

  const handleSelectSurvey = (survey) => {
    setSelectedSurvey(survey);
    setShowSurveyModal(true);
    setFormMenuOpen(false);
  };

  const handleSurveyModalClose = () => {
    setShowSurveyModal(false);
    setSelectedSurvey(null);
  };

  const handleSurveyComplete = () => {
    handleSurveyModalClose();
    onSuccess(`Formulario completado para ${selectedCustomer?.first_name} ${selectedCustomer?.last_name}`);
    onClose();
  };

  const searchDropdownOpen = !selectedCustomer && searchTerm.trim().length > 0 && (loadingSearch || searchResults.length > 0);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
          <ModalHeader borderBottom="1px solid" borderColor="cream.line" color="ink.900" fontWeight="800">
            <HStack spacing={3}>
              <Circle size="38px" bg="lav.bg">
                <Icon as={FiFileText} color="lav.ink" boxSize={4} />
              </Circle>
              <VStack align="start" spacing={0}>
                <Text fontSize="md">Atención</Text>
                <Text fontSize="xs" color="ink.400" fontWeight="500">
                  Busca al alumno y elige la ficha a completar
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box ref={searchAnchorRef}>
                <UnifiedSearchInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  onClear={() => { setSearchTerm(''); setSearchResults([]); setSelectedCustomer(null); }}
                  placeholder="Buscar alumno por nombre, email o RUT..."
                  isSearching={loadingSearch}
                  size="lg"
                  disabled={!!selectedCustomer}
                />
              </Box>

              <AnchoredDropdown anchorRef={searchAnchorRef} isOpen={searchDropdownOpen}>
                {loadingSearch && searchResults.length === 0 ? (
                  <Box textAlign="center" p={3}>
                    <Spinner size="sm" color="coral.500" />
                    <Text fontSize="xs" color="ink.400" mt={1}>Buscando...</Text>
                  </Box>
                ) : (
                  searchResults.map((customer) => (
                    <Box
                      key={customer.id}
                      p={3}
                      cursor="pointer"
                      borderBottom="1px solid"
                      borderColor="cream.line"
                      _last={{ borderBottom: 'none' }}
                      _hover={{ bg: 'coral.tint' }}
                      transition="background .13s"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <HStack spacing={3}>
                        <Circle size="32px" bg="coral.500" color="white" fontSize="xs" fontWeight="700" flexShrink={0}>
                          {getInitials(customer.first_name, customer.last_name)}
                        </Circle>
                        <VStack align="start" spacing={0} flex={1} minW={0}>
                          <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>
                            {customer.name || customer.display_text || `${customer.first_name || ''} ${customer.last_name || ''}`}
                          </Text>
                          <Text fontSize="12px" color="ink.400" noOfLines={1}>
                            {customer.email || customer.username}
                          </Text>
                        </VStack>
                        {customer.group && (
                          <Box as="span" px="8px" py="2px" borderRadius="999px" bg="sky.bg" color="sky.ink"
                            fontSize="11px" fontWeight="700" flexShrink={0}>
                            {customer.group}
                          </Box>
                        )}
                      </HStack>
                    </Box>
                  ))
                )}
              </AnchoredDropdown>

              {selectedCustomer && (
                <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="14px"
                  boxShadow="0 4px 6px -1px rgba(43,39,35,0.06)" p={4}>
                  <HStack justify="space-between" align="center">
                    <HStack spacing={3} flex={1} minW={0}>
                      <Circle size="40px" bg="coral.500" color="white" fontWeight="700" flexShrink={0}>
                        {getInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                      </Circle>
                      <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontWeight="700" fontSize="md" color="ink.900" noOfLines={1}>
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </Text>
                        <Text fontSize="sm" color="ink.400" noOfLines={1}>
                          {selectedCustomer.email || selectedCustomer.username}
                        </Text>
                      </VStack>
                    </HStack>
                    <Tooltip label="Buscar otro alumno">
                      <IconButton
                        size="sm"
                        variant="ghost"
                        color="ink.400"
                        icon={<Icon as={FiX} />}
                        aria-label="Quitar selección"
                        _hover={{ bg: 'cream.bg2', color: 'ink.700' }}
                        onClick={() => { setSelectedCustomer(null); setSearchTerm(''); setSearchResults([]); }}
                      />
                    </Tooltip>
                  </HStack>

                  <Box mt={4}>
                    {atencionForms.length > 0 ? (
                      <>
                        <Button
                          ref={formBtnRef}
                          w="full"
                          bg="coral.500"
                          color="white"
                          fontWeight="700"
                          borderRadius="11px"
                          _hover={{ bg: 'coral.600' }}
                          _active={{ bg: 'coral.700' }}
                          leftIcon={<Icon as={FiFileText} boxSize="14px" />}
                          rightIcon={<Icon as={FiChevronDown} boxSize="14px" />}
                          onClick={() => setFormMenuOpen((open) => !open)}
                        >
                          Llenar formulario
                        </Button>
                        <AnchoredDropdown
                          anchorRef={formBtnRef}
                          isOpen={formMenuOpen}
                          onClose={() => setFormMenuOpen(false)}
                          matchWidth={false}
                          minWidth="260px"
                        >
                          {atencionForms.map((survey) => (
                            <Box
                              key={survey.id}
                              p={3}
                              cursor="pointer"
                              borderBottom="1px solid"
                              borderColor="cream.line"
                              _last={{ borderBottom: 'none' }}
                              _hover={{ bg: 'coral.tint' }}
                              transition="background .13s"
                              onClick={() => handleSelectSurvey(survey)}
                            >
                              <Text fontWeight="600" fontSize="13px" color="ink.900">{survey.name}</Text>
                              {survey.description && (
                                <Text fontSize="11px" color="ink.400" noOfLines={1}>{survey.description}</Text>
                              )}
                            </Box>
                          ))}
                        </AnchoredDropdown>
                      </>
                    ) : (
                      <Text fontSize="13px" color="ink.400" textAlign="center" py={2}>
                        No hay fichas de atención disponibles para tu rol
                      </Text>
                    )}
                  </Box>
                </Box>
              )}

              {!selectedCustomer && (
                <HStack spacing={2} color="ink.400" justify="center" py={2}>
                  <Icon as={FiUser} boxSize={4} />
                  <Text fontSize="13px">Busca un alumno para empezar</Text>
                </HStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal de formulario real — igual que en el perfil */}
      {selectedCustomer && showSurveyModal && selectedSurvey && (
        <ModalAddSurveyResponse
          isOpen={showSurveyModal}
          onClose={handleSurveyModalClose}
          customer={selectedCustomer}
          updateSurveyResponses={handleSurveyComplete}
          directAccess={{ id: selectedSurvey.id, slug: selectedSurvey.slug }}
        />
      )}
    </>
  );
};

export default FormSelectorModal;
